import React, { useEffect, useRef, useState } from 'react'
import LoadingScreen from '../loadingScreenComp/LoadingScreen';
import AlertDialog from '../popupComp/AlertDialog';
import { ThemeTypes } from '../../context/ContextTypes';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import OLMap from 'ol/Map.js'
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { APIResponseWarningLocation, getValidWarningLocations } from '../../services/DashboardService';
import TileLayer from 'ol/layer/Tile';
import { Cluster, OSM } from 'ol/source';
import { Style, Fill, Stroke, Text, Icon, Circle } from 'ol/style'
import GeoJSON from 'ol/format/GeoJSON.js'
import { Feature, Overlay, View } from 'ol';
import { Geometry, Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { warningMap } from '../createWarningComp/FixedDataSet';
import { defaults as defaultControls } from 'ol/control'
import loadLightIcon from '../../images/icons/load-light.gif'
import loadDarkIcon from '../../images/icons/load-dark.gif'
import { ALERT_COLORS } from '../createWarningComp/MapSelectorPopup';
import DashboardPendingCard from './DashboardPendingCard';
import IncidentCountCard from './IncidentCountCard';
import { useNotification } from '../../context/NotificationContext';

type ThemeSeaColorTypes = {
    theme: ThemeTypes;
    seaColor: string;
    borderColor: string;
    borderWidth: number,
    backgroundFill: string;
}
const themeSeaColor: ThemeSeaColorTypes[] = [
    {
        theme: 'dark',
        seaColor: '#080831',
        borderColor: '#7ba6f5ff',
        borderWidth: 1,
        backgroundFill: '#535897ff'
    },
    {
        theme: 'light',
        seaColor: '#e4e4ffff',
        borderColor: '#7ba6f5ff',
        borderWidth: 1,
        backgroundFill: '#7d7fa0ff'
    },
    {
        theme: 'colorful',
        seaColor: '#b3d6f3ff',
        borderColor: '#7ba6f5ff',
        borderWidth: 1,
        backgroundFill: '#aeb2f3ff'
    }
]

type SeverityLevel = {
    white: string, 
    normal: string, 
    yellow: string, 
    amber: string, 
    red: string
}

const iconMap: Map<string, Map<string, string>> = new Map([
    ["GUS", new Map([
        ["white", "/icons/ig.png"],
        ["", ""],
        ["yellow", "/icons/wg.png"],
        ["amber", "/icons/ag.png"],
        ["red", "/icons/eg.png"]
    ])],
    ["LIG", new Map([
        ["white", "/icons/ilh.png"],
        ["", ""],
        ["yellow", "/icons/wlh.png"],
        ["amber", "/icons/alh.png"],
        ["red", "/icons/elh.png"]
    ])],
    ["FLW", new Map([
        ["white", "/icons/if.png"],
        ["", ""],
        ["yellow", "/icons/wf.png"],
        ["amber", "/icons/af.png"],
        ["red", "/icons/ef.png"]
    ])],
    ["TW", new Map([
        ["white", "/icons/it.png"],
        ["", ""],
        ["yellow", "/icons/wt.png"],
        ["amber", "/icons/at.png"],
        ["red", "/icons/et.png"]
    ])],
    ["HRW", new Map([
        ["white", "/icons/ih.png"],
        ["", ""],
        ["yellow", "/icons/wh.png"],
        ["amber", "/icons/ah.png"],
        ["red", "/icons/eh.png"]
    ])],
    ["MWA", new Map([
        ["white", "/icons/im.png"],
        ["", ""],
        ["yellow", "/icons/wm.png"],
        ["amber", "/icons/am.png"],
        ["red", "/icons/em.png"]
    ])],
    ["LSW", new Map([
        ["white", "/icons/il.png"],
        ["", ""],
        ["yellow", "/icons/wl.png"],
        ["amber", "/icons/al.png"],
        ["red", "/icons/el.png"]
    ])],
    ["HWN", new Map([
        ["white", "/icons/iw.png"],
        ["", ""],
        ["yellow", "/icons/ww.png"],
        ["amber", "/icons/aw.png"],
        ["red", "/icons/ew.png"]
    ])],
    ["CUS", new Map([
        ["white", "/icons/ic.png"],
        ["", ""],
        ["yellow", "/icons/wc.png"],
        ["amber", "/icons/ac.png"],
        ["red", "/icons/ec.png"]
    ])],
    ["INC", new Map([
        ["IHRW", "/icons/iconm_heavyrain.png"],
        ["IFLW", "/icons/flood_icon.png"],
        ["ILSS", "assets/flood_icon.png"],
        ["EXPL", "/icons/explosion_icon.png"],
        ["ILSW", "/icons/landslide_icon.png"],
        ["ITHS", "/icons/lightning_icon.png"],
        ["NUCL", "/icons/nuclear_icon.png"],
        ["ITSM", "/icons/tsunami_icon.png"],
        ["IVIR", "/icons/virus_icon.png"],
        ["IFFIR", "/icons/wildfire_icon.png"],
        ["BIOL", "/icons/virus_icon.png"],
        ["IETQ", "/icons/earth_icon.png"],
        ["ICYC", "/icons/cyclone_icon.png"],
        ["IFIR", "/icons/wildfire_icon.png"],
    ])]
])

const HzShortKey: Map<string, string> = new Map<string,string>([
    ["IFFIR", "Wild Fire"],
    ["IFIR", "Fire"],
    ["IFLW", "Flood"],
    ["ILSS", "Landslide symptoms"],
    ["ILSW", "Landslides"],
    ["ICYC", "Cyclone"],
    ["ITSM", "Tsunami"],
    ["ITHS", "Thunderstorm"],
    ["IHRW", "Heavy Rain"],
    ["IETQ", "Earthquake"],
    ["EXPL", "Explosion"],
    ["NUCL", "Nuclear radioactive"],
    ["BIOL", "Biological hazard"]
]);

type DashboardMapPropTypes = {
    gridArea?: string;
}

type IncidentReportTypes = {
    active_alevel: string;
    active_pcode: string;
    adm1_en: string;
    adm1_pcode: string;
    adm2_en: string;
    adm2_pcode: string;
    adm3_en: string;
    adm3_pcode: string;
    adm4_en: string;
    adm4_pcode: string;
    bwater_level: string;
    end_time: string;
    hwater_level: string;
    img_url: string;
    imp_communication: string;
    imp_electricity: string;
    imp_house: string;
    imp_human: string;
    imp_response: string;
    imp_roadblock: string;
    imp_roaddamage: string;
    imp_water: string;
    impdes_communication: string;
    impdes_electricity: string;
    impdes_house: string;
    impdes_human: string;
    impdes_roadblock: string;
    impdes_roaddamage: string;
    impdes_water: string;
    issued_time: string;
    lat: number;
    lon: number;
    observed_date: string;
    occurance_date: string;
    severity: string;
    status: string;
    thumbnail: string;
    tp_number: string;
    video_url: string;
    w_desc: string;
    w_id: string;
    w_name: string;
    w_status: string;
    w_type: string;
    water_houses: string;
    water_level: string;
}

const DashboardMap = (props: DashboardMapPropTypes) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const { theme } = useTheme();
    const navigate = useNavigate()

    const mapRef = useRef<HTMLDivElement>(null)
    const mapObjRef = useRef<OLMap | null>(null)

    const vectorLayerRef = useRef<VectorLayer | null>(null)
    const vectorLayerAdm1Ref = useRef<VectorLayer | null>(null)
    const vectorLayerAdm2Ref = useRef<VectorLayer | null>(null)

    const vectorMarkerLayerRef = useRef<VectorLayer<VectorSource<Feature<Geometry>>, Feature<Geometry>> | null>(null)
    const vectorLayerIncidentMarkerRef = useRef<VectorLayer<VectorSource<Feature<Geometry>>, Feature<Geometry>> | null>(null)

    const [activeLocations, setActiveLocations] = useState<APIResponseWarningLocation[]>([])
    const [incidents, setIncidents] = useState<IncidentReportTypes[]>([])

    const tooltipRef = useRef<HTMLDivElement>(null)

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK
    const [isLoading, setIsLoading] = useState<boolean>(false)
    
    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    const [isNeedReloadingEW, setIsNeedReloadingEW] = useState<boolean>(false)
    const [isNeedReloadingIN, setIsNeedReloadingIN] = useState<boolean>(false)
    
    const { notifications, unreadCount, removeNotification } = useNotification()

    useEffect(() => {
        if (notifications.length > 0) 
        {
            const latest = notifications[0]
            //console.log(`🔔 New: ${latest.title} - ${latest.message}`)
            switch (latest.title)
            {
                case 'NEW_ACTIVE_WARNING':
                    setIsNeedReloadingEW(prevState => !prevState)
                    removeNotification(latest.id)
                    break

                case 'NEW_PENDING_WARNING':
                    setIsNeedReloadingEW(prevState => !prevState)
                    removeNotification(latest.id)
                    break
                case 'NEW_INCIDENT':
                    setIsNeedReloadingIN(prevState => !prevState)
                    removeNotification(latest.id)
                    break
            }
        }
    }, [notifications])

    useEffect(() => {
        if (!mapRef.current || mapObjRef.current) return
        // Base layer (OSM)
        const baseLayer = new TileLayer({
            source: new OSM(),
            zIndex: 15
        })
    
        const vectorLayer = new VectorLayer({
            source: new VectorSource({ url: 'geoJSON/1country_boundary.json', format: new GeoJSON() }),
            style: (feature) => {
                const name = feature.get('ADM_EN')
                const fillColor = themeSeaColor.find(t => t.theme === theme)
                const styles: Style[] = []
                const steps = 3

                for (var i = 0; i < steps; i++) {
                    styles.push(
                        new Style({
                            stroke: new Stroke({
                                color: [51, 255, 255, 1 / (steps - i)],
                                width: (steps - i) * 0.5 - 1,
                            }),
                        })
                    )
                }
                styles.push(
                    new Style({
                        fill: new Fill({ color: fillColor?.backgroundFill }),
                        /* stroke: new Stroke({
                            color: fillColor?.borderColor,
                            width: fillColor?.borderWidth,
                        }), */
                        /* text: new Text({
                            text: name,
                            font: '9px Calibri,sans-serif',
                            fill: new Fill({ color: '#000' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                            overflow: true,
                        }), */
                    })
                )
                return styles
            },
            zIndex: 3
        })

        const vectorLayerAdm1 = new VectorLayer({
            source: new VectorSource({ url: '/geoJSON/maldives/maldives_adm_1.json', format: new GeoJSON() }),
            style: (feature) => {
                const name = feature.get('ADM1_EN')
                const fillColor = themeSeaColor.find(t => t.theme === theme)
                return new Style({
                    fill: new Fill({ color: fillColor?.backgroundFill }),
                    stroke: new Stroke({color: fillColor?.borderColor, width: fillColor?.borderWidth}),
                    /* text: new Text({
                        text: name,
                        font: '12px Calibri,sans-serif',
                        fill: new Fill({ color: '#000' }),
                        stroke: new Stroke({ color: '#fff', width: 2 }),
                        overflow: true,
                    }), */
                })
            },
            zIndex: 5
        })

        const vectorLayerAdm2 = new VectorLayer({
            source: new VectorSource({ url: '/geoJSON/maldives/maldives_adm_2.json', format: new GeoJSON() }),
            style: (feature) => {
                const name = feature.get('ADM2_EN')
                const fillColor = themeSeaColor.find(t => t.theme === theme)
                
                return new Style({
                    fill: new Fill({ color: fillColor?.backgroundFill }),
                    stroke: new Stroke({color: fillColor?.borderColor, width: fillColor?.borderWidth}),
                    /* text: new Text({
                        text: name,
                        font: '12px Calibri,sans-serif',
                        fill: new Fill({ color: '#000' }),
                        stroke: new Stroke({ color: '#fff', width: 2 }),
                        overflow: true,
                    }), */
                })
            },
            zIndex: 10
        })

        // Clustered version
        const clusterSource = new Cluster({
            distance: 40,
            source: new VectorSource()
        })

        const vectorLayerMarker = new VectorLayer({
            source: clusterSource,
            zIndex: 1000,
            style: (feature) => {
                const size = feature.get('features').length
                if (size > 1) {
                    // Cluster style
                    return new Style({
                        image: new Circle({
                            radius: 15,
                            fill: new Fill({ color: '#3399CC' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                        }),
                        text: new Text({
                            text: size.toString(),
                            fill: new Fill({ color: '#fff' }),
                        }),
                    })
                } else {
                    // Single marker
                    const originalFeature = feature.get('features')[0]
                    return new Style({
                        image: new Icon({
                            anchor: [0.5, 1],
                            src: iconMap.get(originalFeature.get('warningType'))?.get(originalFeature.get('severity')),
                            scale: 0.25,
                        }),
                    })
                }
            }
        })

        // Clustered version
        const clusterIncidentSource = new Cluster({
            distance: 40,
            source: new VectorSource()
        })

        const vectorLayerIncidentMarker = new VectorLayer({
            source: clusterIncidentSource,
            zIndex: 1000,
            style: (feature) => {
                const size = feature.get('features').length
                if (size > 1) {
                    // Cluster style
                    return new Style({
                        image: new Circle({
                            radius: 15,
                            fill: new Fill({ color: '#3399CC' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                        }),
                        text: new Text({
                            text: size.toString(),
                            fill: new Fill({ color: '#fff' }),
                        }),
                    })
                } else {
                    // Single marker
                    const originalFeature = feature.get('features')[0]
                    return new Style({
                        image: new Icon({
                            anchor: [0.5, 1],
                            src: iconMap.get('INC')?.get(originalFeature.get('incType')),
                            scale: 0.25,
                        }),
                    })
                }
            }
        })

        // Initialize map
        const map = new OLMap({
            target: mapRef.current,
            layers: [vectorLayer, vectorLayerAdm1, /* vectorLayerAdm2, */ baseLayer, vectorLayerMarker, vectorLayerIncidentMarker],
            view: new View({
                center: fromLonLat([73.2207, 3.2028]), // initial center
                zoom: 6.5,
                maxZoom: 10
            }),
            controls: defaultControls({ zoom: false, attribution: true })
        })

        // Function to update layer visibility based on zoom
        const updateLayerVisibility = () => {
            const zoom = map.getView().getZoom() ?? 0

            vectorLayer.setVisible(true)
            vectorLayerAdm1.setVisible(true)
            vectorLayerAdm2.setVisible(false)
            baseLayer.setVisible(false)

            if (zoom < 6) 
            {
                vectorLayer.setVisible(true)
                vectorLayerAdm1.setVisible(true)
                vectorLayerAdm2.setVisible(false)
                baseLayer.setVisible(false)
            } 
            else if (zoom >= 6 && zoom < 8) 
            {
                vectorLayer.setVisible(true)
                vectorLayerAdm1.setVisible(true)
                vectorLayerAdm2.setVisible(false)
                baseLayer.setVisible(false)
            } 
            else if (zoom >= 8 && zoom < 10) 
            {
                vectorLayer.setVisible(true)
                vectorLayerAdm1.setVisible(true)
                vectorLayerAdm2.setVisible(false)
                baseLayer.setVisible(false)
            } 
            else if (zoom >= 10 && zoom < 12) 
            {
                vectorLayer.setVisible(true)
                vectorLayerAdm1.setVisible(true)
                vectorLayerAdm2.setVisible(false)
                baseLayer.setVisible(false)
            } 
            else 
            {
                // Only baseLayer
                vectorLayer.setVisible(true)
                vectorLayerAdm1.setVisible(true)
                vectorLayerAdm2.setVisible(false)
                baseLayer.setVisible(true)
            }
        }

        // Initial visibility
        updateLayerVisibility()

        map.on('moveend', updateLayerVisibility)   
        mapObjRef.current = map
        vectorLayerRef.current = vectorLayer
        vectorLayerAdm1Ref.current = vectorLayerAdm1
        vectorLayerAdm2Ref.current = vectorLayerAdm2
        vectorMarkerLayerRef.current = vectorLayerMarker
        vectorLayerIncidentMarkerRef.current = vectorLayerIncidentMarker
    }, [])

    useEffect(() => {
        const themeData = themeSeaColor.find(t => t.theme === theme)
        if (!themeData || !mapRef.current) return
        mapRef.current.style.backgroundColor = themeData.seaColor  
    }, [theme])

    const getWarningLocations = async () => {
        setIsLoading(true)

        const response = await getValidWarningLocations()
        if (response.status)
        {
            setActiveLocations(response.data)
        }
        else
        {
            showAlertDialog("Error", "❌ Error getting data from server");
        }
        
        setIsLoading(false)
    }

    const dateTimeFormatter = (data: string): string => {
        const isoString: string = data;
        const date: Date = new Date(isoString);

        const pad = (n: number): string => n.toString().padStart(2, '0');

        const formatted: string =
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}`;

        return formatted
    }

    useEffect(() => {
        getWarningLocations()
        if (!mapObjRef.current || !tooltipRef.current) return;
      
        const tooltip = new Overlay({
            element: tooltipRef.current,
            offset: [-40, -50],
            positioning: 'center-center',
        })
      
        mapObjRef.current.addOverlay(tooltip)
      
        const handlePointerMove = (evt: any) => {
            if (evt.dragging) 
            {
                tooltip.setPosition(undefined)
                return
            }
        
            const feature = mapObjRef.current?.forEachFeatureAtPixel(evt.pixel, (f) => f)
            if (feature && feature.get('features')) 
            {
                const originalFeature = feature.get('features')[0]; // first feature in cluster
                if (originalFeature.get("status") === 'processing' || originalFeature.get("status") === 'initiated')
                {
                    //console.log("DATE TIME -> ", originalFeature.get('validityStart'))
                    tooltipRef.current!.innerHTML = `
                        <span class='title'>${warningMap.get(originalFeature.get('warningType'))} - Process </span>
                        <div class='body'>
                            <div class='row'>
                                <span class="name">Area name: </span>
                                <span class="value">${originalFeature.get('name')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Area code: </span>
                                <span class="value">${originalFeature.get('geoId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Process id: </span>
                                <span class="value">${originalFeature.get('processingId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Current status: </span>
                                <span class="value">${originalFeature.get('status')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Start date: </span>
                                <span class="value">${dateTimeFormatter(originalFeature.get('validityStart'))}</span>
                            </div>
                            <div class='row'>
                                <span class="name">End date: </span>
                                <span class="value">${dateTimeFormatter(originalFeature.get('validityEnd'))}</span>
                            </div>
                        </div>
                    `
                    tooltip.setPosition(evt.coordinate)
                }
                else if (originalFeature.get("status") === 'approved')
                {
                    tooltipRef.current!.innerHTML = `
                        <span class='title'>${warningMap.get(originalFeature.get('warningType'))} - Approved</span>
                        <div class='body'>
                            <div class='row'>
                                <span class="name">Area name: </span>
                                <span class="value">${originalFeature.get('name')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Area code: </span>
                                <span class="value">${originalFeature.get('geoId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Process id: </span>
                                <span class="value">${originalFeature.get('processingId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Current status: </span>
                                <span class="value">${originalFeature.get('status')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Start date: </span>
                                <span class="value">${dateTimeFormatter(originalFeature.get('validityStart'))}</span>
                            </div>
                            <div class='row'>
                                <span class="name">End date: </span>
                                <span class="value">${dateTimeFormatter(originalFeature.get('validityEnd'))}</span>
                            </div>
                        </div>
                    `
                    tooltip.setPosition(evt.coordinate)
                }
                else if (originalFeature.get("status") === 'validate')
                {
                    //console.log("CONS -> ", HzShortKey.get(originalFeature.get('incType')))
                    tooltipRef.current!.innerHTML = `
                        <span class='title'>${HzShortKey.get(originalFeature.get('incType'))}</span>
                        <div class='body'>
                            <div class='row'>
                                <span class="name">Area name: </span>
                                <span class="value">${originalFeature.get('name')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Area code: </span>
                                <span class="value">${originalFeature.get('geoId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Process id: </span>
                                <span class="value">${originalFeature.get('processingId')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Current status: </span>
                                <span class="value">${originalFeature.get('status')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">Occurance date: </span>
                                <span class="value">${originalFeature.get('occurrenceDate')}</span>
                            </div>
                            <div class='row'>
                                <span class="name">End date: </span>
                                <span class="value">${originalFeature.get('endDate')}</span>
                            </div>
                        </div>
                    `
                    tooltip.setPosition(evt.coordinate)
                }
                else 
                {
                    tooltip.setPosition(undefined)
                }
            } 
            else 
            {
                tooltip.setPosition(undefined)
            }
        }
      
        mapObjRef.current.on('pointermove', handlePointerMove)
      
        return () => {
          mapObjRef.current?.un('pointermove', handlePointerMove)
        }
    }, [mapObjRef.current, tooltipRef.current, isNeedReloadingEW])

    const setGeoLocations = (itemList: APIResponseWarningLocation[]): APIResponseWarningLocation[] => {
        const data: APIResponseWarningLocation[] = []
        itemList.forEach((item) => {
            if(!data.some((d) => d.geoId === item.geoId))
            {
                data.push(item);
            }
            else 
            {
                const latOffset = (Math.random() - 0.5) * 0.001;
                const lonOffset = (Math.random() - 0.5) * 0.001;

                const tempData = item;
                item.latitude = item.latitude + latOffset;
                item.longitude = item.longitude + lonOffset;
                data.push(tempData);
            }
        })
        return data;
    }

    const setIncidentGeoLocations = (itemList: IncidentReportTypes[]): IncidentReportTypes[] => {
        const data: IncidentReportTypes[] = []
        itemList.forEach((item) => {
            if(!data.some((d) => d.adm2_pcode === item.adm2_pcode))
            {
                data.push(item);
            }
            else 
            {
                const latOffset = (Math.random() - 0.5) * 0.001;
                const lonOffset = (Math.random() - 0.5) * 0.001;

                const tempData = item;
                item.lat = item.lat + latOffset;
                item.lon = item.lon + lonOffset;
                data.push(tempData);
            }
        })
        return data;
    }

    const warningClusterLayersRef = useRef<Map<string, VectorLayer>>(new Map())
    const createClusterLayer = (warningType: string) => {
        const clusterSource = new Cluster({
            distance: 40,
            source: new VectorSource()
        })
    
        const layer = new VectorLayer({
            source: clusterSource,
            zIndex: 1000,
            style: (feature) => {
                const size = feature.get('features').length
    
                if (size > 1) {
                    return new Style({
                        image: new Circle({
                            radius: 15,
                            fill: new Fill({ color: '#3399CC' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                        }),
                        text: new Text({
                            text: size.toString(),
                            fill: new Fill({ color: '#fff' }),
                        }),
                    })
                } else {
                    const originalFeature = feature.get('features')[0]
    
                    return new Style({
                        image: new Icon({
                            anchor: [0.5, 1],
                            src: iconMap
                                .get(originalFeature.get('warningType'))
                                ?.get(originalFeature.get('severity')),
                            scale: 0.25,
                        }),
                    })
                }
            }
        })
    
        mapObjRef.current?.addLayer(layer)
        warningClusterLayersRef.current.set(warningType, layer)
    }

    useEffect(() => {
        if (!vectorMarkerLayerRef.current || !vectorLayerAdm1Ref.current) return;

        const clusterSource = vectorMarkerLayerRef.current.getSource() as Cluster;
        const source = clusterSource.getSource() as VectorSource;
    
        // Clear previous markers
        source.clear()

        const fixedDate = setGeoLocations(activeLocations)

        fixedDate.forEach((listItem) => {
            const marker = new Feature({
                geometry: new Point(fromLonLat([listItem.longitude, listItem.latitude])),
                name: listItem.name,
                geoId: listItem.geoId,
                warningType: listItem.warningType,
                severity: listItem.severity,
                status: listItem.status,
                processingId: listItem.warningId,
                validityStart: listItem.startDate,
                validityEnd: listItem.endDate
            });
            source.addFeature(marker);

            const layer1 = vectorLayerAdm1Ref.current?.getSource()?.getFeatures()

            let fillColor = themeSeaColor.find(t => t.theme === theme)?.backgroundFill

            if (listItem.status === "initiated" || listItem.status === "processing")
            {
                fillColor = ALERT_COLORS.gray
            } 
            else if (listItem.status === "approved")
            {
                switch (listItem.severity)
                {
                    case "white":
                        fillColor = ALERT_COLORS.white;
                        break;
                    case "":
                        fillColor = ALERT_COLORS.green;
                        break;
                    case "yellow":
                        fillColor = ALERT_COLORS.yellow;
                        break;
                    case "amber":
                        fillColor = ALERT_COLORS.amber;
                        break;
                    case "red":
                        fillColor = ALERT_COLORS.red;
                        break;
                }
            }
        
            layer1?.forEach(feature  => {
                const code = feature.get('ADM4_PCODE') || feature.get('ADM3_PCODE') || feature.get('ADM2_PCODE') || feature.get('ADM1_PCODE') || feature.get('ADM_PCODE');
                if (code === listItem.geoId)
                {
                    feature.setStyle(
                        new Style({
                            fill: new Fill({ color: fillColor || ALERT_COLORS.gray }),
                            stroke: new Stroke({ color: '#000000', width: 1 }),
                        })
                    )
                }
            })
        })
    }, [activeLocations])

    useEffect(() => {
        setIsLoading(true)
        const params = new URLSearchParams()
        params.append("arealist", "LK1333")
        params.append("arealist", "LK1312")

        fetch (`${BASE_URL}/hz/api/v1/getHzmByLocationTwoWeek?${params.toString()}`, {
            method: 'GET'
        })
        .then(response => response.json())
        .then(data => {
            const response = data as []
            setIncidents(response as IncidentReportTypes[])
        })
        .catch(error => console.log(error))
        .finally(() => {
            setIsLoading(false)
        })
    }, [isNeedReloadingIN])

    useEffect(() => {
        if (!vectorLayerIncidentMarkerRef.current) return;

        const clusterSource = vectorLayerIncidentMarkerRef.current.getSource() as Cluster;
        const source = clusterSource.getSource() as VectorSource;

        // Clear previous markers
        source.clear()

        const fixedDate = setIncidentGeoLocations(incidents)
        
        fixedDate.forEach((listItem) => {
            //console.log("WARNING TYPE -> ", listItem.occurance_date)
            const marker = new Feature({
                geometry: new Point(fromLonLat([listItem.lon, listItem.lat])),
                name: listItem.adm2_en,
                geoId: listItem.adm2_pcode,
                warningType: listItem.w_type,
                incType: listItem.w_type,
                status: listItem.status,
                processingId: listItem.w_id,
                occurrenceDate: listItem.occurance_date,
                endDate: listItem.end_time
            });
            source.addFeature(marker);
        })
    }, [incidents])

    return (
        <div className="dashboard" style={{gridArea: props.gridArea ? props.gridArea : ''}}>
            <div className="dashboard-map" style={{ position: 'relative' }}>
                <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: '0.5rem', backgroundColor: 'var(--color-main-black)', overflow: 'hidden' }}> </div>
                <div ref={tooltipRef} className="ol-tooltip"></div>
            </div>
            {/* <div className='overlap-wrapper'>
                <DashboardPendingCard gridArea=''/>
                <IncidentCountCard gridArea=""/>
            </div> */}
            {isLoading && (
                <div className='loading'>
                    <img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} />
                </div>
            )}
        </div>
    )
}

export default DashboardMap